import React, { useContext } from 'react';
import { useLocation } from '@reach/router';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { Context } from '@components/Context';
import Colors from '@components/shared/Colors';
import { P } from '@components/shared/Typography';
import { CartItem, ProductCode, SubscriptionCartItem, CartItemType } from '@contracts';
import Pad from '@images/cart/pad-icon.png';
import Tampon from '@images/cart/tampon-icon.png';
import {
  hasTamponsAndPads,
  getBoxItemsCount,
  getBoxTamponsCount,
  getBoxPadsCount,
  filterBoxItemTampons,
  filterBoxItemPads,
  convertBoxItems
} from '@utils/cart';
import { replaceText } from '@utils/handleTextReplacement';
import { isPadsStackBox } from '@utils/pads';
import CartItemPadsBoxItems from './CartItemPadsBoxItems';

interface Props {
  item: CartItem & SubscriptionCartItem;
  icon?: string;
}

const CartItemBoxItems: React.FC<Props> = ({ item }) => {
  const { t } = useTranslation();
  const {
    state: { country }
  } = useContext(Context);
  const location = useLocation();
  const boxItems = convertBoxItems(item.boxItems, item.productCode, country);
  const boxItemsCount = getBoxItemsCount(boxItems);
  const boxTamponsCount = getBoxTamponsCount(boxItems);
  const boxPadsCount = getBoxPadsCount(boxItems);
  const planCode = item.selectedPlan?.planCode ?? false;
  const isMultiMonth = planCode && (planCode.includes('3_month') || planCode?.includes('6_month') || planCode.includes('12_month'));
  const isPads = item.productCode === ProductCode.Pads;
  const isStackBox = isPadsStackBox(country);
  const itemType = isPads ? t('components.pads').toLowerCase() : t('components.tampons').toLowerCase();

  const getBoxItemTitle = () => {
    if (planCode) {
      if (isPads && isStackBox) {
        if (planCode.includes('3_month')) {
          return t('components.box-content-description-3-month');
        }

        if (planCode.includes('1_month')) {
          return t('components.box-content-description-1-month');
        }
      }

      if (isMultiMonth) {
        return t('components.box-content-description-multi-month', {
          itemsCount: boxItemsCount,
          installments: item.delivery.installments,
          itemsType: itemType
        });
      }
    }

    return t('components.box-content-description', {
      itemsCount: boxItemsCount,
      itemsType: itemType
    });
  };

  if (hasTamponsAndPads(item)) {
    let title = '';
    let delivery = '';
    if (item.type === CartItemType.OneOffPurchase) {
      title = t('components.box-content-description-tampons-pads');
    } else if (planCode && planCode.includes('3_month')) {
      title = t('components.box-content-pads-tampons-3month', {
        tampons: boxTamponsCount,
        pads: boxPadsCount
      });
      delivery = t('components.delivered-at-once');
    } else {
      title = t('components.box-content-pads-tampons-multi-month', {
        tampons: boxTamponsCount,
        pads: boxPadsCount
      });
      delivery = t('components.delivered-in', { installments: item.delivery.installments });
    }

    const tamponsBoxItems = filterBoxItemTampons(item.boxItems || []);
    const padsBoxItems = filterBoxItemPads(item.boxItems || []);

    return (
      <>
        <Separator space={'10px'} />
        <BoxItemsTitle>
          <div>{title}</div>
          <div>{delivery}</div>
        </BoxItemsTitle>
        <Separator space={'10px'} />
        <TypeSection marginLeft={'3px'}>
          <img src={Tampon} width="10px" height="18px" />
          <BoxItems>
            {tamponsBoxItems
              .filter((bi) => bi.count > 0)
              .map((boxItem) => {
                const boxItemTitle = replaceText(t(`components.${boxItem.key}`), country, location);
                return (
                  <BoxItem key={boxItem.name}>
                    <ItemSizer>
                      <span>{boxItem.count}</span>&times; {boxItemTitle}
                    </ItemSizer>
                  </BoxItem>
                );
              })}
          </BoxItems>
        </TypeSection>
        <Separator space={'10px'} />
        <TypeSection marginLeft={'0'}>
          <img src={Pad} width="13px" height="18px" />
          <CartItemPadsBoxItems boxItems={padsBoxItems} cartItem={item} />
        </TypeSection>
      </>
    );
  }

  return (
    <>
      <BoxItemsTitle>{getBoxItemTitle()}</BoxItemsTitle>
      {isPads && isStackBox ? (
        <CartItemPadsBoxItems boxItems={boxItems} cartItem={item} />
      ) : (
        <BoxItems>
          {boxItems
            .filter((bi) => bi.count > 0)
            .map((boxItem) => {
              const boxItemTitle = replaceText(t(`components.${boxItem.key}`), country, location);
              return (
                <BoxItem key={boxItem.name}>
                  <span>{boxItem.count}</span>&times; {boxItemTitle}
                </BoxItem>
              );
            })}
        </BoxItems>
      )}
    </>
  );
};

export default CartItemBoxItems;

const Separator = styled.div<{ space: string }>`
  width: 100%;
  height: ${(p) => p.space};
`;

const ItemSizer = styled.div`
  width: 105px;
`;

const BoxItems = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
`;

const BoxItemsTitle = styled(P)`
  font-family: BrownStd;
  font-weight: bold;
  font-size: 12px;
  line-height: 22px;
  color: ${Colors.navyBlue};
`;

const BoxItem = styled.div`
  font-family: BrownStd;
  font-size: 12px;
  line-height: 22px;
  letter-spacing: -0.03em;
  margin-right: 5px;
  text-align: left;
  color: ${Colors.navyBlue};

  span {
    font-weight: bold;
    margin-right: 4px;
  }
`;

const TypeSection = styled.div<{ marginLeft: string }>`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0px;
  gap: 10px;
  flex: none;
  flex-grow: 0;

  img {
    margin-top: 5px;
    margin-left: ${(p) => p.marginLeft};
  }
`;
